/**
 * The shape of every URL that leads to the auth screens.
 *
 * Login used to be reachable as /auth, /auth/login and sibling routes declared in
 * three route tables, and an ended session was flagged three different ways
 * (`?expiredSession`, `?expiredSession&reason=idle`, a bare `expiredSession`). Each
 * variant was a separate page to analytics and a separate case for the screen to
 * get wrong. Everything that builds or reads one of these URLs goes through here.
 */

import { END_REASON } from './constants';

export const AUTH_BASE_PATH = '/auth';
export const LOGIN_PATH = `${AUTH_BASE_PATH}/login`;

/** The views the auth screen renders, as they appear in the URL. */
export const AUTH_VIEWS = ['login', 'register', 'register-step-2', 'forgot-password', 'reset-password'];

/**
 * One route for every view, with the view optional so the bare /auth alias lands
 * on the same Route. A single Route keeps the screen mounted while the user moves
 * between login, register and recovery: separate Routes remounted it on every
 * switch, dropping what had been typed and replaying any notice on the way back.
 */
export const AUTH_ROUTE_PATH = `${AUTH_BASE_PATH}/:view(${AUTH_VIEWS.join('|')})?`;

/** Why the session ended, as `?session_ended=<reason>`. */
export const SESSION_ENDED_PARAM = 'session_ended';

// Earlier builds wrote these, and links and history entries holding them are
// still around, so they are read — never written.
const LEGACY_FLAG_PARAM = 'expiredSession';
const LEGACY_REASON_PARAM = 'reason';

/**
 * What the /auth alias carries over to the login route besides the session-end
 * flag. `token` opens the reset-password view; `jwt`, `userId` and `email` are the
 * SSO hand-off App.js reads from whatever URL it lands on, so a redirect that drops
 * them breaks it. Anything else only splits one page into many in analytics.
 */
const CARRIED_PARAMS = ['token', 'jwt', 'userId', 'email'];

/** Every way a session can end, except signing out on purpose — that is not news. */
export const isAnnounceableReason = (reason) =>
  typeof reason === 'string' &&
  reason !== END_REASON.LOGOUT &&
  Object.values(END_REASON).includes(reason);

const toSearch = (params) => {
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const hasSessionEndParams = (search) => {
  const params = new URLSearchParams(search || '');
  return params.has(SESSION_ENDED_PARAM) || params.has(LEGACY_FLAG_PARAM);
};

/**
 * The reason a URL claims, or null. The legacy flag carried no reason of its own,
 * so it reads as a plain expiry unless a valid `reason` came with it.
 *
 * Only a claim: anyone can type it. Whether it is announced is decided by
 * sessionEndNotice, not by the URL.
 */
export const readSessionEndReason = (search) => {
  const params = new URLSearchParams(search || '');

  if (params.has(SESSION_ENDED_PARAM)) {
    const reason = params.get(SESSION_ENDED_PARAM);
    return isAnnounceableReason(reason) ? reason : null;
  }

  if (params.has(LEGACY_FLAG_PARAM)) {
    const reason = params.get(LEGACY_REASON_PARAM);
    return isAnnounceableReason(reason) ? reason : END_REASON.EXPIRED;
  }

  return null;
};

/** The same query without the session-end flag, in either format. Other params are untouched. */
export const stripSessionEndParams = (search) => {
  const params = new URLSearchParams(search || '');
  if (params.has(LEGACY_FLAG_PARAM)) params.delete(LEGACY_REASON_PARAM);
  params.delete(LEGACY_FLAG_PARAM);
  params.delete(SESSION_ENDED_PARAM);
  return toSearch(params);
};

/**
 * The query the /auth alias forwards: the carried params, plus the session-end
 * flag rewritten to its current format. Everything else is dropped.
 */
export const normalizeAuthSearch = (search) => {
  const source = new URLSearchParams(search || '');
  const params = new URLSearchParams();

  CARRIED_PARAMS.forEach((key) => {
    if (source.has(key)) params.set(key, source.get(key));
  });

  const reason = readSessionEndReason(search);
  if (reason) params.set(SESSION_ENDED_PARAM, reason);

  return toSearch(params);
};

/** Where to send someone to sign in, saying why their session ended when there is a reason to. */
export const buildLoginLocation = (reason) => ({
  pathname: LOGIN_PATH,
  search: isAnnounceableReason(reason)
    ? toSearch(new URLSearchParams({ [SESSION_ENDED_PARAM]: reason }))
    : '',
});

export const buildLoginUrl = (reason) => {
  const { pathname, search } = buildLoginLocation(reason);
  return `${pathname}${search}`;
};
